import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  activePatientId: null,
  activeTab: 'observations', // 'observations' | 'documents' | 'prescriptions' | 'antecedents' | 'timeline'
  draftObservationNote: '',
  selectedAntecedentCategory: null,
  selectedMedicalBackgroundCategory: null,
  observationFilter: '',
  showRemindersModal: false,
  medicalCondToggle: false,
  // Chart Value Dialog State
  showChartValueDialog: false,
  selectedChartPropertyForValue: null,
  chartValueInput: '',
  externalChartPoint: null,
  // Cancellation UI State
  cancelModalOpen: false,
  selectedCancelReason: null,
  // Absence UI State
  absenceModalOpen: false,
  selectedAbsenceReason: null,
  // Reschedule & Availability UI State
  rescheduleModalOpen: false,
  selectedRescheduleDate: null,
  selectedRescheduleTime: null,
  selectedRescheduleReason: null,
};

export const consultationUiSlice = createSlice({
  name: 'consultationUi',
  initialState,
  reducers: {
    setActivePatientId: (state, action) => {
      state.activePatientId = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setDraftObservationNote: (state, action) => {
      state.draftObservationNote = action.payload;
    },
    clearDraftObservationNote: (state) => {
      state.draftObservationNote = '';
    },
    setSelectedAntecedentCategory: (state, action) => {
      state.selectedAntecedentCategory = action.payload;
    },
    setSelectedMedicalBackgroundCategory: (state, action) => {
      state.selectedMedicalBackgroundCategory = action.payload;
    },
    setObservationFilter: (state, action) => {
      state.observationFilter = action.payload;
    },
    setShowRemindersModal: (state, action) => {
      state.showRemindersModal = Boolean(action.payload);
    },
    setMedicalCondToggle: (state, action) => {
      state.medicalCondToggle = Boolean(action.payload);
    },
    // Chart Value Dialog Actions
    openChartValueDialog: (state, action) => {
      state.showChartValueDialog = true;
      state.selectedChartPropertyForValue = action.payload?.property ?? action.payload ?? null;
      if (action.payload?.initialValue !== undefined) {
        state.chartValueInput = action.payload.initialValue;
      }
    },
    closeChartValueDialog: (state) => {
      state.showChartValueDialog = false;
      state.selectedChartPropertyForValue = null;
      state.chartValueInput = '';
    },
    setChartValueInput: (state, action) => {
      state.chartValueInput = action.payload;
    },
    setExternalChartPoint: (state, action) => {
      state.externalChartPoint = action.payload;
    },
    // Cancellation Actions
    setCancelModalOpen: (state, action) => {
      state.cancelModalOpen = Boolean(action.payload);
      if (!action.payload) {
        state.selectedCancelReason = null;
      }
    },
    setSelectedCancelReason: (state, action) => {
      state.selectedCancelReason = action.payload;
    },
    resetCancelState: (state) => {
      state.cancelModalOpen = false;
      state.selectedCancelReason = null;
    },
    // Absence Actions
    setAbsenceModalOpen: (state, action) => {
      state.absenceModalOpen = Boolean(action.payload);
      if (!action.payload) {
        state.selectedAbsenceReason = null;
      }
    },
    setSelectedAbsenceReason: (state, action) => {
      state.selectedAbsenceReason = action.payload;
    },
    resetAbsenceState: (state) => {
      state.absenceModalOpen = false;
      state.selectedAbsenceReason = null;
    },
    // Reschedule Actions
    setRescheduleModalOpen: (state, action) => {
      state.rescheduleModalOpen = Boolean(action.payload);
      if (!action.payload) {
        state.selectedRescheduleTime = null;
        state.selectedRescheduleReason = null;
      }
    },
    setSelectedRescheduleDate: (state, action) => {
      state.selectedRescheduleDate = action.payload;
    },
    setSelectedRescheduleTime: (state, action) => {
      state.selectedRescheduleTime = action.payload;
    },
    setSelectedRescheduleReason: (state, action) => {
      state.selectedRescheduleReason = action.payload;
    },
    resetRescheduleState: (state) => {
      state.rescheduleModalOpen = false;
      state.selectedRescheduleDate = null;
      state.selectedRescheduleTime = null;
      state.selectedRescheduleReason = null;
    },
    resetConsultationUi: (state) => {
      state.draftObservationNote = '';
      state.observationFilter = '';
      state.showRemindersModal = false;
      state.medicalCondToggle = false;
      state.selectedMedicalBackgroundCategory = null;
      state.showChartValueDialog = false;
      state.selectedChartPropertyForValue = null;
      state.chartValueInput = '';
      state.externalChartPoint = null;
      state.cancelModalOpen = false;
      state.selectedCancelReason = null;
      state.absenceModalOpen = false;
      state.selectedAbsenceReason = null;
      state.rescheduleModalOpen = false;
      state.selectedRescheduleDate = null;
      state.selectedRescheduleTime = null;
      state.selectedRescheduleReason = null;
    },
  },
});

export const {
  setActivePatientId,
  setActiveTab,
  setDraftObservationNote,
  clearDraftObservationNote,
  setSelectedAntecedentCategory,
  setSelectedMedicalBackgroundCategory,
  setObservationFilter,
  setShowRemindersModal,
  setMedicalCondToggle,
  openChartValueDialog,
  closeChartValueDialog,
  setChartValueInput,
  setExternalChartPoint,
  setCancelModalOpen,
  setSelectedCancelReason,
  resetCancelState,
  setAbsenceModalOpen,
  setSelectedAbsenceReason,
  resetAbsenceState,
  setRescheduleModalOpen,
  setSelectedRescheduleDate,
  setSelectedRescheduleTime,
  setSelectedRescheduleReason,
  resetRescheduleState,
  resetConsultationUi,
} = consultationUiSlice.actions;

// Selectors
export const selectConsultationUi = (state) => state.consultationUi;
export const selectActivePatientId = (state) => state.consultationUi?.activePatientId;
export const selectActiveTab = (state) => state.consultationUi?.activeTab;
export const selectDraftObservationNote = (state) => state.consultationUi?.draftObservationNote;
export const selectSelectedAntecedentCategory = (state) => state.consultationUi?.selectedAntecedentCategory;
export const selectSelectedMedicalBackgroundCategory = (state) => state.consultationUi?.selectedMedicalBackgroundCategory ?? null;
export const selectObservationFilter = (state) => state.consultationUi?.observationFilter;
export const selectShowRemindersModal = (state) => state.consultationUi?.showRemindersModal;
export const selectMedicalCondToggle = (state) => state.consultationUi?.medicalCondToggle ?? false;

// Chart Dialog Selectors
export const selectShowChartValueDialog = (state) => state.consultationUi?.showChartValueDialog ?? false;
export const selectSelectedChartPropertyForValue = (state) => state.consultationUi?.selectedChartPropertyForValue ?? null;
export const selectChartValueInput = (state) => state.consultationUi?.chartValueInput ?? '';
export const selectExternalChartPoint = (state) => state.consultationUi?.externalChartPoint ?? null;

// Cancellation Selectors
export const selectCancelModalOpen = (state) => state.consultationUi?.cancelModalOpen;
export const selectSelectedCancelReason = (state) => state.consultationUi?.selectedCancelReason;
export const selectCancelUiState = (state) => ({
  isOpen: state.consultationUi?.cancelModalOpen ?? false,
  reason: state.consultationUi?.selectedCancelReason ?? null,
});

// Absence Selectors
export const selectAbsenceModalOpen = (state) => state.consultationUi?.absenceModalOpen;
export const selectSelectedAbsenceReason = (state) => state.consultationUi?.selectedAbsenceReason;
export const selectAbsenceUiState = (state) => ({
  isOpen: state.consultationUi?.absenceModalOpen ?? false,
  reason: state.consultationUi?.selectedAbsenceReason ?? null,
});

// Reschedule Selectors
export const selectRescheduleModalOpen = (state) => state.consultationUi?.rescheduleModalOpen;
export const selectSelectedRescheduleDate = (state) => state.consultationUi?.selectedRescheduleDate;
export const selectSelectedRescheduleTime = (state) => state.consultationUi?.selectedRescheduleTime;
export const selectSelectedRescheduleReason = (state) => state.consultationUi?.selectedRescheduleReason;
export const selectRescheduleUiState = (state) => ({
  isOpen: state.consultationUi?.rescheduleModalOpen ?? false,
  date: state.consultationUi?.selectedRescheduleDate ?? null,
  time: state.consultationUi?.selectedRescheduleTime ?? null,
  reason: state.consultationUi?.selectedRescheduleReason ?? null,
});

export default consultationUiSlice.reducer;
